import { bytesToMegabytes } from "@/lib/utils";
import type { BackgroundVisitUpload } from "@/store/backgroundVisitUpload.store";
import { CircularProgress } from "../CircularProgress";
import { Button } from "../button";
import { Play, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { BackgroundVisitUploadStatus } from "@/common/enums/backgroundVisitUploadStatus";
import { t } from "i18next";

interface Props {
  backgroundVisitUpload: BackgroundVisitUpload;
  onCancel: (id: string) => void;
  onStart: (id: string) => void;
}

export const BackgroundVisitUploadItem = ({
  backgroundVisitUpload,
  onCancel,
  onStart,
}: Props): JSX.Element => {
  return (
    <div className="flex items-center justify-between py-4 px-6">
      <div className="space-y-1 flex-grow">
        <h3 className="text-primary text-md font-bold">
          {backgroundVisitUpload.id}
        </h3>
        <span className="text-sm">
          {bytesToMegabytes(backgroundVisitUpload.data.file.size).toFixed(2)} MB
        </span>
      </div>

      <div className="flex items-center gap-2">
        <AnimatePresence mode="wait">
          {backgroundVisitUpload.status ===
            BackgroundVisitUploadStatus.UPLOADING && (
            <motion.div
              key="uploading"
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center gap-2"
              exit={{ opacity: 0, scale: 0.8 }}
              initial={{ opacity: 0, scale: 0.8 }}
            >
              <CircularProgress
                size={40}
                strokeWidth={5}
                value={backgroundVisitUpload.progress}
              />
              <Button
                size={"sm"}
                variant="destructive"
                onClick={() => {
                  onCancel(backgroundVisitUpload.id);
                }}
              >
                <X />
              </Button>
            </motion.div>
          )}

          {backgroundVisitUpload.status ===
            BackgroundVisitUploadStatus.DONE && (
            <motion.span
              key="done"
              animate={{ opacity: 1, y: 0 }}
              className="font-bold text-success"
              exit={{ opacity: 0, y: -10 }}
              initial={{ opacity: 0, y: 10 }}
            >
              {t("backgroundVisitUpload.status.done")}
            </motion.span>
          )}

          {backgroundVisitUpload.status ===
            BackgroundVisitUploadStatus.PENDING && (
            <motion.div
              key="pending"
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              initial={{ opacity: 0, scale: 0.8 }}
            >
              <Button
                size={"sm"}
                variant="success"
                onClick={() => {
                  onStart(backgroundVisitUpload.id);
                }}
              >
                <Play />
              </Button>
            </motion.div>
          )}
          {backgroundVisitUpload.status ===
            BackgroundVisitUploadStatus.FAILED && (
            <motion.span
              key="failed"
              animate={{ opacity: 1 }}
              className="font-bold text-error"
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
            >
              {t("backgroundVisitUpload.status.failed")}
            </motion.span>
          )}
          {backgroundVisitUpload.status ===
            BackgroundVisitUploadStatus.CANCELLED && (
            <motion.span
              key="cancelled"
              animate={{ opacity: 1 }}
              className="font-bold text-error"
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
            >
              {t("backgroundVisitUpload.status.cancelled")}
            </motion.span>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
